import { DecimalPipe } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AssetSelectorComponent } from '../../shared/design-system/asset-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { AssetMaster, CatalogService } from './catalog.service';

@Component({
  standalone: true,
  selector: 'ain-asset-register',
  imports: [DecimalPipe, AinPageComponent, AinStateComponent, TableModule, ButtonModule, FormsModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('catalog.assets.register.title')" [description]="t('catalog.assets.register.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else {
        <section class="panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('catalog.action.create')" (click)="openCreate()"></button>
            <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="load()"></button>
            <button pButton type="button" [label]="showFilters() ? t('catalog.filter.hide') : t('catalog.filter.show')" severity="secondary" (click)="showFilters.set(!showFilters())"></button>
          </div>
          @if (showFilters()) {
            <div class="filters">
              <label>
                <span>{{ t('catalog.tracking.status') }}</span>
                <p-select
                  [options]="statusOptions"
                  [(ngModel)]="filters.status"
                  optionLabel="label"
                  optionValue="value"
                  [showClear]="true"
                  appendTo="body"
                  (ngModelChange)="load()" />
              </label>
            </div>
          }
          <p-table [value]="rows()" [paginator]="true" [rows]="15">
            <ng-template pTemplate="header">
              <tr>
                <th style="width: 3rem"><input type="checkbox" aria-label="select all" /></th>
                <th>{{ t('catalog.assets.assetNumber') }}</th>
                <th>{{ t('catalog.assets.name') }}</th>
                <th>{{ t('catalog.tracking.status') }}</th>
                <th>{{ t('catalog.assets.netBookValue') }}</th>
                <th>{{ t('catalog.assets.costCenter') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td><input type="checkbox" [attr.aria-label]="row.assetNumber" /></td>
                <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.assetNumber }}</button></td>
                <td>{{ row.name }}</td>
                <td>{{ row.status }}</td>
                <td>{{ row.netBookValue | number:'1.2-2' }}</td>
                <td>{{ row.costCenterCode }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .filters { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; }
    .filters label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
  `]
})
export class AssetRegisterComponent implements OnInit {
  private readonly service = inject(CatalogService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly rows = signal<AssetMaster[]>([]);
  readonly showFilters = signal(false);
  readonly filters = { status: '' };

  readonly statusOptions = [
    { label: '—', value: '' },
    { label: 'Registered', value: 'Registered' },
    { label: 'InService', value: 'InService' },
    { label: 'UnderMaintenance', value: 'UnderMaintenance' },
    { label: 'Disposed', value: 'Disposed' }
  ];

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.service.listAssets(this.filters.status.trim() || undefined).subscribe({
      next: rows => {
        this.rows.set(rows);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  openDetail(id: string): void {
    void this.router.navigate(['/assets/register', id]);
  }

  openCreate(): void {
    void this.router.navigate(['/assets/register', 'new']);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
